<script setup lang="ts">
import { ref, watch } from 'vue'
import { useAuthStore } from '@/stores/auth';
import RegisterForm from './RegisterForm.vue';
import { useRouter } from 'vue-router';

const router = useRouter()


// import { Button, Dialog, InputText } from 'primevue'
const authStore = useAuthStore()
const showRegister = ref<boolean>(false)
const closeDialogPlease = ref<boolean>(false)
const visible = defineModel<boolean>()
const headerText = ref<string>('Login')
const password = ref<string>('')

const gdprAck = ref<boolean>(false)
const showGDPRMessage = ref<boolean>(false)
const showMissingFieldsMessage = ref<boolean>(false)
const showInvalidLoginMessage = ref<boolean>(false)
const showUnspecifiedErrorMessage = ref<boolean>(false)
const showPendingApprovalMessage = ref<boolean>(false)
const loginErrorMessage = ref<string>('')
const qaCookies = ref<boolean>(false)

const toggleDialog = () => {
  visible.value = !visible.value
  console.log('visible:', visible.value)
}

const toggleRegister = () => {
  showRegister.value = !showRegister.value
  if (showRegister.value) {
    headerText.value = 'Register'
  }
}

const tryLogin = async () => {
  if (authStore.username === '' || password.value === '') {
    showMissingFieldsMessage.value = true
    return
  } else {
    // Reset all error messages
    showMissingFieldsMessage.value = false
    showGDPRMessage.value = false
    showInvalidLoginMessage.value = false
    showUnspecifiedErrorMessage.value = false
    showPendingApprovalMessage.value = false
    loginErrorMessage.value = ''
    
    try {
      const response = await authStore.login(authStore.username, password.value)
      if (response.ok) {
        // Login successful - errors already cleared
        visible.value = false
      } else if (response.status === 401) {
        // Get the error message from the response
        const errorData = await response.json()
        const errorMessage = errorData.message || 'Login failed'
        
        if (errorMessage.includes('pending admin approval') || errorMessage.includes('pending approval')) {
          showPendingApprovalMessage.value = true
          loginErrorMessage.value = errorMessage
        } else {
          showInvalidLoginMessage.value = true
          loginErrorMessage.value = errorMessage
        }
      } else {
        showUnspecifiedErrorMessage.value = true
      }
    } catch (error) {
      console.error('Login error:', error)
      showUnspecifiedErrorMessage.value = true
    }
  }
}

const loginWithMicrosoft = () => {
  // Redirect to the Django Microsoft OAuth login endpoint
  window.location.href = '/accounts/microsoft/login/'
}

const loginWithGoogle = () => {
  // Redirect to the Django Google OAuth login endpoint
  window.location.href = '/accounts/google/login/'
}

// watch the authStore.isAuthenticated value
watch(() => authStore.isAuthenticated, (newVal: boolean) => {
  if (newVal) {
    headerText.value = 'Logout'
  } else {
    headerText.value = 'Login'
  }
})

// Watch for the Register form closing
watch(() => showRegister.value, (newVal: boolean) => {
  if (!newVal) {
    headerText.value = 'Login'
  }
})

// Watch for a call to close everything
watch( () => closeDialogPlease.value, (newVal: boolean) => {
  if (newVal) {
    visible.value = false
    closeDialogPlease.value = false
  }
})
</script>

<template>
<Dialog
  v-model:visible="visible"
  modal
  :header="headerText"
  :style="{ width: showRegister ? '42rem' : '30rem', maxWidth: '95vw' }"
>
    <div class="container">
        <div v-if="!showRegister">
            <div v-if="!authStore.isAuthenticated">
                <h1>Welcome to StARRS</h1>
                <p class="text-surface-500 dark:text-surface-400 mb-4">
                  Please sign in with your Microsoft account or use your username and password.
                </p>
                <div class="mb-4">
                    <Button 
                      type="button"
                      class="w-full p-button-primary microsoft-login-btn"
                      @click="loginWithMicrosoft"
                    >
                      <i class="pi pi-microsoft mr-2"></i>
                      Sign in with Microsoft
                    </Button>
                </div>
                <div class="mb-4">
                    <Button 
                      type="button"
                      class="w-full google-login-btn"
                      @click="loginWithGoogle"
                    >
                      <i class="pi pi-google mr-2"></i>
                      Sign in with Google
                    </Button>
                </div>
                <div class="mb-4 text-center">
                  <span class="or-divider">OR</span>
                </div>
                <form @submit.prevent="tryLogin" class="login-form">
                  <div class="form-group mb-3">
                    <label for="username">Username</label>
                    <InputText 
                      id="username"
                      v-model="authStore.username" 
                      placeholder="Enter your username" 
                      class="w-full"
                    />
                  </div>
                  
                  <div class="form-group mb-3">
                    <label for="password">Password</label>
                    <InputText 
                      id="password"
                      v-model="password" 
                      type="password"
                      placeholder="Enter your password" 
                      toggleMask
                      class="w-full"
                    />
                  </div>
                  
                  <div class="flex align-items-center mb-3">
                    <Checkbox v-model="qaCookies" binary id="qaCookies" />
                    <label for="qaCookies" class="ml-2">
                      Accept optional cookies for performance monitoring
                    </label>
                  </div>
                  
                  <div class="error-container mb-3">
                    <div v-if="showMissingFieldsMessage" class="p-error block">
                      Please fill in both username and password.
                    </div>
                    <div v-if="showInvalidLoginMessage" class="p-error block">
                      {{ loginErrorMessage || 'Invalid username or password.' }}
                    </div>
                                        <div v-if="showPendingApprovalMessage" class="pending-approval-message mb-2">
                      <i class="pi pi-exclamation-triangle" style="color: #f59e0b; margin-right: 0.5rem;"></i>
                      <span>{{ loginErrorMessage }}</span>
                    </div>
                    <div v-if="showUnspecifiedErrorMessage" class="p-error block">
                      An unexpected error occurred. Please try again later.
                    </div>
                  </div>
                  
                  <div class="flex justify-content-between mb-3">
                    <Button type="submit" label="Sign In" class="p-button-primary" />
                    <Button type="button" label="Register" class="p-button-outlined" @click="showRegister = true" />
                  </div>
                </form>
            </div>
            <div v-else>
                <div class="text-center mb-4">
                  <i class="pi pi-user-plus text-primary" style="font-size: 3rem"></i>
                  <h2 class="mt-2">You are logged in</h2>
                  <p class="text-surface-500 dark:text-surface-400">
                    You are currently signed in as <strong>{{ authStore.username }}</strong>
                  </p>
                </div>
                <Divider />
                <div class="flex justify-content-center">
                    <Button type="button" label="Sign Out" icon="pi pi-sign-out" @click="authStore.logout()" />
                </div>
            </div>
        </div>
        <div v-else>
            <RegisterForm v-model:showRegister="showRegister" v-model:closeDialogPlease="closeDialogPlease" />
        </div>
    </div>
</Dialog>
</template>

<style scoped>
.container {
  padding: 1rem;
}

.microsoft-login-btn {
  background-color: #0078d4 !important;
  border-color: #0078d4 !important;
}

.microsoft-login-btn:hover {
  background-color: #106ebe !important;
  border-color: #106ebe !important;
}

.google-login-btn {
  background-color: #db4437 !important;
  border-color: #db4437 !important;
}

.google-login-btn:hover {
  background-color: #c23321 !important;
  border-color: #c23321 !important;
}

.or-divider {
  position: relative;
  display: inline-block;
  color: #666;
  font-size: 0.9rem;
}

.or-divider::before,
.or-divider::after {
  content: '';
  position: absolute;
  top: 50%;
  width: 60px;
  height: 1px;
  background-color: #ccc;
}

.or-divider::before {
  right: 100%;
  margin-right: 15px;
}

.or-divider::after {
  left: 100%;
  margin-left: 15px;
}

.login-form {
  display: flex;
  flex-direction: column;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.error-container {
  min-height: 1.5rem;
}

.p-error {
  color: #dc2626;
  background-color: #fec7c7;
  padding: 0.75rem;
  border-radius: 0.375rem;
  border-left: 4px solid #f59e0b;
  margin: 0.5rem 0;
  display: flex;
  align-items: center;
}

.pending-approval-message {
  color: #381e00;
  background-color: #fef3c7;
  padding: 0.75rem;
  border-radius: 0.375rem;
  border-left: 4px solid #f59e0b;
  margin: 0.5rem 0;
  display: flex;
  align-items: center;
}

.p-warning {
  background-color: #fef3c7;
  padding: 0.75rem;
  border-radius: 0.375rem;
  border-left: 4px solid #f59e0b;
  margin: 0.5rem 0;
  display: flex;
  align-items: center;
}

h1 {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: var(--primary-color);
}

h2 {
  font-size: 1.25rem;
  margin-top: 0;
  margin-bottom: 0.5rem;
}
</style>